/**
 * Boutons : un seul gabarit pour toute l'application. Variantes et tailles
 * sont des classes de `styles/index.css`, jamais des couleurs ecrites ici.
 */
import { useEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Check, Copy, LoaderCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type VarianteBouton = 'primaire' | 'secondaire' | 'discret' | 'danger' | 'danger-doux';
export type TailleBouton = 'xs' | 'sm' | 'md' | 'lg';

export function classesBouton(
  variante: VarianteBouton = 'secondaire',
  taille: TailleBouton = 'md',
): string {
  return `bouton bouton-${taille} bouton-${variante}`;
}

/** Indicateur d'activite : purement decoratif, le texte porte le sens. */
export function Rouet({ className = '' }: { className?: string }) {
  return <LoaderCircle aria-hidden="true" className={`animate-spin ${className}`} />;
}

interface ProprietesBouton extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBouton;
  taille?: TailleBouton;
  /** Affiche un rouet et neutralise le bouton, sans changer sa largeur ni lui retirer le focus. */
  chargement?: boolean;
  /** Icone placee avant le libelle. */
  icone?: ReactNode;
  /** Pour replacer le focus sur ce bouton quand celui qui l'avait disparait. */
  ref?: Ref<HTMLButtonElement>;
}

export function Bouton({
  variante = 'secondaire',
  taille = 'md',
  chargement = false,
  icone,
  className = '',
  type = 'button',
  disabled,
  onClick,
  'aria-disabled': indisponible,
  'aria-busy': occupe,
  children,
  ...reste
}: ProprietesBouton) {
  return (
    <button
      {...reste}
      type={type}
      className={`${classesBouton(variante, taille)} ${className}`}
      // Pendant un chargement, `aria-disabled` et non `disabled` : un bouton
      // desactive perd le focus (formulaire, fenetre), qui tomberait sur la page.
      disabled={disabled}
      aria-disabled={chargement || indisponible || undefined}
      aria-busy={chargement || occupe || undefined}
      onClick={(evenement) => {
        // Garde contre le double envoi : `preventDefault` retient aussi la soumission du formulaire (clic, Entree).
        if (chargement) {
          evenement.preventDefault();
          return;
        }
        onClick?.(evenement);
      }}
    >
      {chargement ? <Rouet /> : icone}
      {children}
    </button>
  );
}

type ProprietesBoutonIcone = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> & {
  /** Nom accessible et infobulle : un bouton sans texte doit tout de meme se nommer. */
  libelle: string;
  icone: LucideIcon;
  variante?: VarianteBouton;
  taille?: TailleBouton;
  /** Pour replacer le focus sur ce bouton apres une action (fleches de reordonnancement). */
  ref?: Ref<HTMLButtonElement>;
};

export function BoutonIcone({
  libelle,
  icone: Icone,
  variante = 'discret',
  taille = 'sm',
  className = '',
  type = 'button',
  ...reste
}: ProprietesBoutonIcone) {
  return (
    <button
      type={type}
      aria-label={libelle}
      title={libelle}
      className={`${classesBouton(variante, taille)} bouton-icone ${className}`}
      {...reste}
    >
      <Icone />
    </button>
  );
}

/** Bouton de copie dans le presse-papiers, avec retour visuel et vocal. */
export function BoutonCopier({
  valeur,
  libelle = 'Copier',
  libelleCopie = 'Copié',
  taille = 'sm',
  variante = 'secondaire',
  className = '',
}: {
  valeur: string;
  libelle?: string;
  libelleCopie?: string;
  taille?: TailleBouton;
  variante?: VarianteBouton;
  className?: string;
}) {
  const [copie, setCopie] = useState(false);
  const refMinuteur = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (refMinuteur.current !== null) window.clearTimeout(refMinuteur.current);
    },
    [],
  );

  const copier = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(valeur);
    } catch {
      // Presse-papiers refuse (contexte non securise) : le texte reste
      // selectionnable a la main, on n'affiche pas de faux succes.
      return;
    }
    setCopie(true);
    if (refMinuteur.current !== null) window.clearTimeout(refMinuteur.current);
    refMinuteur.current = window.setTimeout(() => setCopie(false), 2000);
  };

  return (
    <Bouton
      taille={taille}
      variante={variante}
      className={className}
      icone={copie ? <Check /> : <Copy />}
      onClick={() => void copier()}
    >
      {copie ? libelleCopie : libelle}
      <span className="sr-only" role="status">
        {copie ? 'Copié dans le presse-papiers' : ''}
      </span>
    </Bouton>
  );
}
